/**
 * ── Raw Bible text tokenizer ───────────────────────────────────────────────
 *
 * The source text carries legacy marks from the MyBible format that the app
 * published on Play (2022) already rendered and that this app **discarded**:
 *
 *   `<FI>…</FI>`  words supplied by the translator → **italics**. This is an
 *                 KJV editorial convention ("that all *men* through him
 *                 might believe"), not decoration: it distinguishes what is in the
 *                 original from what was added to make the sentence work.
 *   `<FR>…</FR>`  words of Jesus → **red letters** (`redLetter` token).
 *   `<RF>…</RF>`  translator's note → tappable glyph that opens the note.
 *   `<CM>`        paragraph mark (no representation of its own here).
 *   `<WH430>` / `<WG3056>` / `<XWH4438>`  referencia Strong (hebreo/griego).
 *
 * Closing tags may be escaped (`<\/RF>`), which is how they appear in
 * some source-text dumps; the scanner accepts both forms.
 */

export interface VerseTextToken {
  type: "text";
  text: string;
  /** Word supplied by the translator (`<FI>`): rendered in italics. */
  italic: boolean;
  /** Words of Jesus (`<FR>`): rendered with the `redLetter` theme token. */
  redLetter: boolean;
}

export interface StrongToken {
  type: "strong";
  code: string;
  label: string;
}

export interface TranslatorNoteToken {
  type: "note";
  /** Note text, with marks already removed. */
  text: string;
}

type VerseToken = VerseTextToken | StrongToken | TranslatorNoteToken;

/**
 * Recognized tags. Single sweep: open/close style, note and Strong.
 * `<FO>` (OT quotation in the NT) is accepted and ignored as styling, just as
 * the legacy did, but without leaving the tag on screen.
 */
const MARKUP = /<CM>|<(FI|FO|FR|RF)>|<\\?\/(FI|FO|FR|RF)>|<(X?W[GH])(\d+)>/gi;

interface ScanState {
  italic: number;
  red: number;
  /** Buffer for the translator's note while `<RF>` is open. */
  note: string | null;
}

function pushText(out: VerseToken[], text: string, state: ScanState): void {
  if (text === "") {
    return;
  }
  if (state.note !== null) {
    state.note += text;
    return;
  }
  const last = out.at(-1);
  const italic = state.italic > 0;
  const redLetter = state.red > 0;
  // Merge contiguous runs with the same style so the paragraph is not fragmented into
  // dozens of <span> elements, which would break line wrapping between words.
  if (last?.type === "text" && last.italic === italic && last.redLetter === redLetter) {
    last.text += text;
    return;
  }
  out.push({ type: "text", text, italic, redLetter });
}

function openTag(tag: string, state: ScanState): void {
  const upper = tag.toUpperCase();
  if (upper === "FI") {
    state.italic += 1;
  } else if (upper === "FR") {
    state.red += 1;
  } else if (upper === "RF" && state.note === null) {
    state.note = "";
  }
}

function closeTag(tag: string, out: VerseToken[], state: ScanState): void {
  const upper = tag.toUpperCase();
  if (upper === "FI") {
    state.italic = Math.max(0, state.italic - 1);
  } else if (upper === "FR") {
    state.red = Math.max(0, state.red - 1);
  } else if (upper === "RF" && state.note !== null) {
    const note = state.note.trim();
    state.note = null;
    if (note !== "") {
      out.push({ type: "note", text: note });
    }
  }
}

/**
 * Tokenizes the raw text of a verse.
 *
 * With `hasStrong === false`, Strong marks are removed completely (the reader
 * can hide them at will); the rest of the formatting is ALWAYS preserved—
 * hiding the numbers must not change the verse's typography.
 */
export function formatVerseText(text: string, hasStrong = true): VerseToken[] {
  const out: VerseToken[] = [];
  const state: ScanState = { italic: 0, red: 0, note: null };
  let lastIndex = 0;

  MARKUP.lastIndex = 0;
  let match = MARKUP.exec(text);
  while (match !== null) {
    pushText(out, text.slice(lastIndex, match.index), state);
    const [, open, close, strongPrefix, strongNumber] = match;

    if (open) {
      openTag(open, state);
    } else if (close) {
      closeTag(close, out, state);
    } else if (strongPrefix && strongNumber) {
      if (hasStrong && state.note === null) {
        const code = `${strongPrefix.toUpperCase().endsWith("H") ? "H" : "G"}${strongNumber}`;
        out.push({ type: "strong", code, label: code });
      }
    }
    // `<CM>` lands here without a branch: it is consumed without emitting anything.

    ({ lastIndex } = MARKUP);
    match = MARKUP.exec(text);
  }

  pushText(out, text.slice(lastIndex), state);

  // Unclosed `<RF>`: we do not lose the rest of the verse—it is returned as
  // normal text, which is what the reader expects to see.
  if (state.note !== null && state.note !== "") {
    const pending = state.note;
    state.note = null;
    pushText(out, pending, state);
  }

  if (out.length === 0) {
    return [{ type: "text", text: "", italic: false, redLetter: false }];
  }
  return out;
}

/**
 * Plain verse text, with no marks—for copying, sharing,
 * printing, and displaying fragments. The translator's note is NOT included (it is
 * critical apparatus, not the verse).
 */
export function plainVerseText(text: string): string {
  return formatVerseText(text, false)
    .map((token) => (token.type === "text" ? token.text : ""))
    .join("");
}
